import ReactPlayer from 'react-player';
import {
  MediaController,
  MediaControlBar,
  MediaTimeRange,
  MediaTimeDisplay,
  MediaVolumeRange,
  MediaPlaybackRateButton,
  MediaPlayButton,
  MediaSeekBackwardButton,
  MediaSeekForwardButton,
  MediaMuteButton,
  MediaFullscreenButton,
} from 'media-chrome/react';
// Import Media Chrome styles
import 'media-chrome';
import tw, { styled } from 'twin.macro';

interface HLSVideoProps {
  src?: string;
  controls?: boolean;
  fullWidth?: boolean;
  mirror?: boolean;
  controlsList?: string;
}

const VideoContainer = styled.div<{ $fullWidth?: boolean }>(({ $fullWidth }) => [
  tw`w-full rounded-2xl overflow-hidden`,
  !$fullWidth && tw`lg:max-w-[500px] xl:w-[500px]`,
]);

const PlayerContainer = styled.div<{ $mirror?: boolean }>(({ $mirror }) => [
  tw`w-full aspect-[4/3] relative`,
  $mirror && {
    '& [slot="media"]': {
      transform: 'rotateY(180deg)',
    },
  },
]);

const StyledMediaControlBar = styled(MediaControlBar as any)(() => [
  {
    // Ensure controls are always visible and properly sized on mobile
    'display': 'flex',
    'flex-wrap': 'nowrap',
    'padding': '0px 0px',
    'gap': '0px',

    // Responsive adjustments for mobile
    '@media (max-width: 640px)': {
      padding: '0px 0px',
      gap: '0px',

      // Hide less essential controls on mobile to prevent overflow
      'media-seek-backward-button, media-seek-forward-button': {
        display: 'none',
      },
      'media-volume-range': {
        display: 'none',
      },
      'media-playback-rate-button': {
        display: 'none',
      },

      // Ensure essential buttons are properly sized for touch
      'media-play-button, media-mute-button, media-fullscreen-button': {
        minWidth: '44px',
        minHeight: '44px',
      },

      // Make time display more compact on mobile
      'media-time-display': {
        fontSize: '12px',
        padding: '0 4px',
      },
    },

    // Tablet adjustments
    '@media (min-width: 641px) and (max-width: 1024px)': {
      gap: '0px',

      'media-playback-rate-button': {
        display: 'none',
      },
    },
  },
]);

/**
 * HLS-capable video player with Media Chrome controls.
 *
 * Features:
 * - HLS and multi-format support via react-player
 * - Professional Media Chrome control bar
 * - Play/pause, seek, timeline, volume, speed, fullscreen
 * - Backward/forward seek buttons (10s)
 * - Mirror support for webcam recordings
 * - Mobile-responsive controls with touch-friendly button sizes
 * - Adaptive control visibility (simplified controls on mobile)
 */
const HLSVideo: React.FC<HLSVideoProps> = ({
  src,
  controls = true,
  fullWidth = false,
  mirror = false,
}) => {
  if (!src) {
    return null;
  }

  const Player = ReactPlayer as any;

  return (
    <VideoContainer $fullWidth={fullWidth}>
      <PlayerContainer $mirror={mirror}>
        <MediaController
          style={{
            width: '100%',
            height: '100%',
            aspectRatio: '4/3',
          }}
        >
          <Player
            slot="media"
            src={src}
            // POC-251: Load-bearing — do not remove. Chrome 142+ ships a native HLS
            // demuxer, so react-player hands our playlist straight to <video> instead
            // of hls.js. That demuxer rejects media segments that tainted the origin
            // ("insecure media request" -> MediaError code 4 with no console output),
            // and our page, playlist and segments live on three different origins.
            // Requesting CORS keeps the responses untainted so playback is allowed.
            crossOrigin="anonymous"
            controls={false}
            width="100%"
            height="100%"
            playsInline
            style={{
              width: '100%',
              height: '100%',
              '--controls': 'none',
            } as any}
          />
          {controls && (
            <StyledMediaControlBar>
              <MediaPlayButton />
              {/* <MediaSeekBackwardButton seekOffset={10} />
              <MediaSeekForwardButton seekOffset={10} /> */}
              <MediaTimeRange />
              <MediaTimeDisplay showDuration />
              <MediaMuteButton />
              <MediaVolumeRange />
              <MediaPlaybackRateButton />
              <MediaFullscreenButton />
            </StyledMediaControlBar>
          )}
        </MediaController>
      </PlayerContainer>
    </VideoContainer>
  );
};

HLSVideo.displayName = 'HLSVideo';

export default HLSVideo;
